import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/contexts/CartContext";
import { RestaurantCartProvider } from "@/contexts/RestaurantCartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import BottomNav from "@/components/BottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import WelcomeSplash from "@/components/WelcomeSplash";

// Eagerly load Index (homepage) for fast FCP
import Index from "./pages/Index";

// Lazy-load all other pages
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const StorePage = lazy(() => import("./pages/StorePage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const NewServicePage = lazy(() => import("./pages/NewServicePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const NotificationSettingsPage = lazy(() => import("./pages/NotificationSettingsPage"));
const TaxiPage = lazy(() => import("./pages/TaxiPage"));
const RestaurantPage = lazy(() => import("./pages/RestaurantPage"));
const RestaurantCartPage = lazy(() => import("./pages/RestaurantCartPage"));
const RestaurantCheckoutPage = lazy(() => import("./pages/RestaurantCheckoutPage"));
const RestaurantOrdersPage = lazy(() => import("./pages/RestaurantOrdersPage"));
const CarMarketPage = lazy(() => import("./pages/CarMarketPage"));
const RealEstatePage = lazy(() => import("./pages/RealEstatePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdPolicyPage = lazy(() => import("./pages/AdPolicyPage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const PostJobPage = lazy(() => import("./pages/PostJobPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const RestaurantsListPage = lazy(() => import("./pages/RestaurantsListPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStores = lazy(() => import("./pages/admin/AdminStores"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminDrivers = lazy(() => import("./pages/admin/AdminDrivers"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSetup = lazy(() => import("./pages/admin/AdminSetup"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminRestaurants = lazy(() => import("./pages/admin/AdminRestaurants"));
const AdminTaxiRides = lazy(() => import("./pages/admin/AdminTaxiRides"));
const AdminFinance = lazy(() => import("./pages/admin/AdminFinance"));
const AdminMapsHealth = lazy(() => import("./pages/admin/AdminMapsHealth"));

// Vendor
const VendorRegister = lazy(() => import("./pages/vendor/VendorRegister"));
const VendorLayout = lazy(() => import("./pages/vendor/VendorLayout"));
const VendorDashboard = lazy(() => import("./pages/vendor/VendorDashboard"));
const VendorProducts = lazy(() => import("./pages/vendor/VendorProducts"));
const VendorOrders = lazy(() => import("./pages/vendor/VendorOrders"));
const VendorStore = lazy(() => import("./pages/vendor/VendorStore"));
const VendorStats = lazy(() => import("./pages/vendor/VendorStats"));
const VendorPixelAnalytics = lazy(() => import("./pages/vendor/VendorPixelAnalytics"));
const VendorFinance = lazy(() => import("./pages/vendor/VendorFinance"));
const VendorSettings = lazy(() => import("./pages/vendor/VendorSettings"));
const VendorChat = lazy(() => import("./pages/vendor/VendorChat"));
const VendorCoupons = lazy(() => import("./pages/vendor/VendorCoupons"));

// Restaurant Owner
const RestaurantRegister = lazy(() => import("./pages/restaurant/RestaurantRegister"));
const RestaurantLayout = lazy(() => import("./pages/restaurant/RestaurantLayout"));
const RestaurantDashboard = lazy(() => import("./pages/restaurant/RestaurantDashboard"));
const RestaurantMenu = lazy(() => import("./pages/restaurant/RestaurantMenu"));
const RestaurantStore = lazy(() => import("./pages/restaurant/RestaurantStore"));
const RestaurantSettingsPage = lazy(() => import("./pages/restaurant/RestaurantSettings"));
const RestaurantOrders = lazy(() => import("./pages/restaurant/RestaurantOrders"));

// Driver
const DriverRegister = lazy(() => import("./pages/driver/DriverRegister"));
const DriverLayout = lazy(() => import("./pages/driver/DriverLayout"));
const DriverDashboard = lazy(() => import("./pages/driver/DriverDashboard"));
const DriverAvailableOrders = lazy(() => import("./pages/driver/DriverAvailableOrders"));
const DriverTracking = lazy(() => import("./pages/driver/DriverTracking"));
const DriverProfile = lazy(() => import("./pages/driver/DriverProfile"));
const DriverTaxiRides = lazy(() => import("./pages/driver/DriverTaxiRides"));

const queryClient = new QueryClient();

const CustomerLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="max-w-lg mx-auto min-h-screen bg-background relative">
    <WelcomeSplash />
    {children}
    <BottomNav />
  </div>
);

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <SettingsProvider>
        <CartProvider>
          <RestaurantCartProvider>
          <FavoritesProvider>
            <Toaster />
            <Sonner />
            <PWAInstallPrompt />
            
            <BrowserRouter>
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
              <Routes>
                {/* Admin Setup */}
                <Route path="/shiber/setup" element={<AdminSetup />} />

                {/* Admin Routes */}
                <Route path="/shiber" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="stores" element={<AdminStores />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="drivers" element={<AdminDrivers />} />
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="support" element={<AdminSupport />} />
                  <Route path="restaurants" element={<AdminRestaurants />} />
                  <Route path="taxi" element={<AdminTaxiRides />} />
                  <Route path="finance" element={<AdminFinance />} />
                  <Route path="maps-health" element={<AdminMapsHealth />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                {/* Vendor Routes */}
                <Route path="/vendor/register" element={<VendorRegister />} />
                <Route path="/vendor" element={<VendorLayout />}>
                  <Route index element={<VendorDashboard />} />
                  <Route path="products" element={<VendorProducts />} />
                  <Route path="orders" element={<VendorOrders />} />
                  <Route path="store" element={<VendorStore />} />
                  <Route path="chat" element={<VendorChat />} />
                  <Route path="coupons" element={<VendorCoupons />} />
                  <Route path="stats" element={<VendorStats />} />
                  <Route path="pixel-analytics" element={<VendorPixelAnalytics />} />
                  <Route path="finance" element={<VendorFinance />} />
                  <Route path="settings" element={<VendorSettings />} />
                </Route>

                {/* Restaurant Owner Routes */}
                <Route path="/restaurant/register" element={<RestaurantRegister />} />
                <Route path="/my-restaurant" element={<RestaurantLayout />}>
                  <Route index element={<RestaurantDashboard />} />
                  <Route path="menu" element={<RestaurantMenu />} />
                  <Route path="orders" element={<RestaurantOrders />} />
                  <Route path="store" element={<RestaurantStore />} />
                  <Route path="settings" element={<RestaurantSettingsPage />} />
                </Route>

                {/* Driver Routes */}
                <Route path="/driver/register" element={<DriverRegister />} />
                <Route path="/driver" element={<DriverLayout />}>
                  <Route index element={<DriverDashboard />} />
                  <Route path="available" element={<DriverAvailableOrders />} />
                  <Route path="taxi" element={<DriverTaxiRides />} />
                  <Route path="tracking" element={<DriverTracking />} />
                  <Route path="profile" element={<DriverProfile />} />
                </Route>

                {/* Customer Routes */}
                <Route path="/" element={<CustomerLayout><Index /></CustomerLayout>} />
                <Route path="/index" element={<CustomerLayout><Index /></CustomerLayout>} />
                <Route path="/search" element={<CustomerLayout><SearchPage /></CustomerLayout>} />
                <Route path="/favorites" element={<CustomerLayout><FavoritesPage /></CustomerLayout>} />
                <Route path="/chat" element={<CustomerLayout><ChatPage /></CustomerLayout>} />
                <Route path="/cart" element={<CustomerLayout><CartPage /></CustomerLayout>} />
                <Route path="/profile" element={<CustomerLayout><ProfilePage /></CustomerLayout>} />
                <Route path="/product/:id" element={<CustomerLayout><ProductPage /></CustomerLayout>} />
                <Route path="/store/:id" element={<CustomerLayout><StorePage /></CustomerLayout>} />
                <Route path="/checkout" element={<CustomerLayout><CheckoutPage /></CustomerLayout>} />
                <Route path="/orders" element={<CustomerLayout><OrdersPage /></CustomerLayout>} />
                <Route path="/service/:id" element={<CustomerLayout><ServicePage /></CustomerLayout>} />
                <Route path="/services" element={<CustomerLayout><ServicesPage /></CustomerLayout>} />
                <Route path="/restaurants-list" element={<CustomerLayout><RestaurantsListPage /></CustomerLayout>} />
                <Route path="/services/new" element={<CustomerLayout><NewServicePage /></CustomerLayout>} />
                <Route path="/notifications" element={<CustomerLayout><NotificationsPage /></CustomerLayout>} />
                <Route path="/notification-settings" element={<NotificationSettingsPage />} />
                <Route path="/taxi" element={<CustomerLayout><TaxiPage /></CustomerLayout>} />
                <Route path="/restaurant/:id" element={<CustomerLayout><RestaurantPage /></CustomerLayout>} />
                <Route path="/restaurant-cart" element={<CustomerLayout><RestaurantCartPage /></CustomerLayout>} />
                <Route path="/restaurant-checkout" element={<CustomerLayout><RestaurantCheckoutPage /></CustomerLayout>} />
                <Route path="/restaurant-orders" element={<CustomerLayout><RestaurantOrdersPage /></CustomerLayout>} />
                <Route path="/cars" element={<CustomerLayout><CarMarketPage /></CustomerLayout>} />
                <Route path="/real-estate" element={<CustomerLayout><RealEstatePage /></CustomerLayout>} />
                <Route path="/privacy" element={<CustomerLayout><PrivacyPage /></CustomerLayout>} />
                <Route path="/about" element={<CustomerLayout><AboutPage /></CustomerLayout>} />
                <Route path="/terms" element={<CustomerLayout><TermsPage /></CustomerLayout>} />
                <Route path="/contact" element={<CustomerLayout><ContactPage /></CustomerLayout>} />
                <Route path="/ad-policy" element={<CustomerLayout><AdPolicyPage /></CustomerLayout>} />
                <Route path="/jobs" element={<CustomerLayout><JobsPage /></CustomerLayout>} />
                <Route path="/job/:id" element={<CustomerLayout><JobDetailPage /></CustomerLayout>} />
                <Route path="/jobs/post" element={<CustomerLayout><PostJobPage /></CustomerLayout>} />

                {/* Auth Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </FavoritesProvider>
          </RestaurantCartProvider>
        </CartProvider>
        </SettingsProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
